import type { ContentActionPayload } from '~/features/analytix/ui/models/content-actions'
import type { ContentWatchedCommand } from './index.h'

import { IconNames } from "/app/libraries/arcana/src/atoms/Icon/index.ts"

import { api } from '@setplex/tria-api'
import { attach, createEvent, sample } from 'effector'
import { t } from '~/core/i18n/i18next'
import { register } from '~/core/repository'
import {
  entitiesNotificationToastsModel,
  NotificationToastType,
} from '~/entities/notification-toasts'
import { contentAction } from '~/features/analytix/ui/models/content-actions'

export const setContentWatched = createEvent<ContentWatchedCommand>()

const watchedUpdated = createEvent<{ watched: boolean }>()

export const updateWatchedVodFx = attach({
  effect: api.vods.updateVodFx,
})

export const markEpisodeWatchedByUserFx = attach({
  effect: api.tvshows.postMarkEpisodeWatchedFx,
})

export const markEpisodeWatchedWithoutSeasonByUserFx = attach({
  effect: api.tvshows.postMarkEpisodeWatchedWithoutSeasonFx,
})

sample({
  clock: setContentWatched,
  filter: (command) => Boolean(command.analyticsUiElementType),
  fn: (command): ContentActionPayload => ({
    contentId: command.type === 'vod' ? command.id : command.tvShowId,
    contentType:
      command.analyticsContentType ??
      (command.type === 'vod' ? 'vod' : 'tvshow'),
    contentName: command.analyticsContentName ?? command.name,
    programName: command.analyticsProgramName,
    routeName: command.analyticsRouteName,
    uiElementType: command.analyticsUiElementType!,
    uiElementName: command.watched ? 'mark_as_watched' : 'unmark_as_watched',
    actionName: 'tap',
  }),
  target: contentAction,
})

sample({
  clock: setContentWatched,
  source: api.vods.updateVodFx.pending,
  filter: (pending, command) => !pending && command.type === 'vod',
  fn: (_, command) => {
    if (command.type !== 'vod') throw new Error('Unexpected watched command')

    return {
      id: command.id,
      watched: command.watched,
    }
  },
  target: updateWatchedVodFx,
})

sample({
  clock: setContentWatched,
  source: api.tvshows.postMarkEpisodeWatchedFx.pending,
  filter: (pending, command) =>
    !pending && command.type === 'episodeWithSeason',
  fn: (_, command) => {
    if (command.type !== 'episodeWithSeason') {
      throw new Error('Unexpected watched command')
    }

    return {
      tvShowId: command.tvShowId,
      seasonId: command.seasonId,
      episodeId: command.episodeId,
      watched: command.watched,
    }
  },
  target: markEpisodeWatchedByUserFx,
})

sample({
  clock: setContentWatched,
  source: api.tvshows.postMarkEpisodeWatchedWithoutSeasonFx.pending,
  filter: (pending, command) =>
    !pending && command.type === 'episodeWithoutSeason',
  fn: (_, command) => {
    if (command.type !== 'episodeWithoutSeason') {
      throw new Error('Unexpected watched command')
    }

    return {
      tvShowId: command.tvShowId,
      episodeId: command.episodeId,
      watched: command.watched,
    }
  },
  target: markEpisodeWatchedWithoutSeasonByUserFx,
})

sample({
  clock: updateWatchedVodFx.done,
  filter: ({ params }) => typeof params.watched === 'boolean',
  fn: ({ params }) => ({ watched: Boolean(params.watched) }),
  target: watchedUpdated,
})

sample({
  clock: [
    markEpisodeWatchedByUserFx.done,
    markEpisodeWatchedWithoutSeasonByUserFx.done,
  ],
  fn: ({ params }) => ({ watched: params.watched }),
  target: watchedUpdated,
})

sample({
  clock: watchedUpdated,
  fn: ({ watched }) => ({
    message: t(watched ? 'marked_as_watched' : 'unmarked_as_watched'),
    type: NotificationToastType.SUCCESS,
    icon: watched ? IconNames.eyeOpen : IconNames.eyeCrossed,
  }),
  target: entitiesNotificationToastsModel.showToast,
})

register(setContentWatched, 'features/content-watched/setContentWatched')
